/**
 * Auth bridge for the browser-extension side panel.
 *
 * The extension embeds this app in a *third-party iframe* whose storage is
 * partitioned, so Clerk finds no session and every GraphQL call goes out
 * unauthenticated (the workspace then renders as an empty "solid box").
 *
 * To fix that without weakening the normal web-app auth, the extension posts its
 * access token into the iframe via `postMessage`. We accept it ONLY from
 * a `chrome-extension://` origin and only when actually embedded; the backend
 * still validates the token, so a bad token grants nothing.
 */
let embeddedToken: string | null = null;
let resolveToken: ((token: string) => void) | null = null;
const tokenPromise = new Promise<string>(resolve => {
  resolveToken = resolve;
});

/** True when running inside an iframe (the extension side panel embeds us). */
export const isEmbeddedContext = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true; // cross-origin access throws → we are embedded
  }
};

/** The injected token, if one has arrived. */
export const getEmbeddedToken = (): string | null => embeddedToken;

/**
 * Resolve with the injected token, waiting up to `timeoutMs` for it to arrive
 * (the postMessage handshake is async and may land after the first query fires).
 */
export const awaitEmbeddedToken = (timeoutMs = 4000): Promise<string | null> => {
  if (embeddedToken) return Promise.resolve(embeddedToken);
  return Promise.race([tokenPromise, new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs))]);
};

/**
 * Extract a token from a postMessage, or null if it must be rejected. Only
 * chrome-extension origins and the CORTEX_AUTH_TOKEN shape are accepted.
 */
export const acceptTokenMessage = (origin: string, data: unknown): string | null => {
  if (!origin.startsWith('chrome-extension://')) return null;
  const d = data as { type?: string; token?: unknown } | null;
  if (d?.type === 'CORTEX_AUTH_TOKEN' && typeof d.token === 'string' && d.token) return d.token;
  return null;
};

/** Install the message listener + ask the extension for a token. Idempotent. */
export const initEmbeddedAuthBridge = (): void => {
  if (!isEmbeddedContext()) return;
  window.addEventListener('message', event => {
    const token = acceptTokenMessage(event.origin, event.data);
    if (token) {
      embeddedToken = token;
      resolveToken?.(token);
    }
  });
  try {
    window.parent.postMessage({ type: 'CORTEX_AUTH_REQUEST' }, '*');
  } catch {
    /* parent unreachable — ignore */
  }
};
